import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';

import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';

import Main from 'layouts/Main';

const EventCarousel = ({ section, onImageClick }) => {
  const scrollRef = useRef(null);
  const [activeDot, setActiveDot] = useState(0);

  useEffect(() => {
    const scroller = scrollRef.current;

    if (!scroller) {
      return undefined;
    }

    const interval = setInterval(() => {
      const cardWidth = scroller.firstChild
        ? scroller.firstChild.getBoundingClientRect().width + 24
        : 324;
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      const nextLeft =
        scroller.scrollLeft + cardWidth >= maxScroll
          ? 0
          : scroller.scrollLeft + cardWidth;

      scroller.scrollTo({ left: nextLeft, behavior: 'smooth' });
    }, 2800);

    return () => clearInterval(interval);
  }, []);

  const updateActiveDot = () => {
    const scroller = scrollRef.current;

    if (!scroller) {
      return;
    }

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const progress = maxScroll > 0 ? scroller.scrollLeft / maxScroll : 0;
    setActiveDot(Math.round(progress * (section.photos.length - 1)));
  };

  const goToPhoto = (index) => {
    const scroller = scrollRef.current;

    if (!scroller) {
      return;
    }

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const nextLeft =
      section.photos.length > 1
        ? (maxScroll / (section.photos.length - 1)) * index
        : 0;

    scroller.scrollTo({ left: nextLeft, behavior: 'smooth' });
    setActiveDot(index);
  };

  return (
    <Box sx={{ mb: { xs: 7, md: 9 } }}>
      <Typography
        variant="h5"
        align="left"
        fontWeight={800}
        sx={{ mb: 3 }}
      >
        {section.title}
      </Typography>

      <Box
        ref={scrollRef}
        onScroll={updateActiveDot}
        sx={{
          display: 'flex',
          gap: 3,
          overflowX: 'auto',
          scrollBehavior: 'smooth',
          scrollSnapType: 'x mandatory',
          pb: 2,
          mx: 0,
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.24)',
            borderRadius: 8,
          },
        }}
      >
        {section.photos.map((photo, index) => (
          <Box
            key={photo}
            sx={{
              flex: '0 0 auto',
              width: { xs: '82%', sm: 340, md: 360 },
              height: { xs: 230, sm: 260, md: 280 },
              overflow: 'hidden',
              borderRadius: 2,
              scrollSnapAlign: 'start',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            }}
          >
            <LazyLoadImage
              src={photo}
              alt={`${section.title}-${index + 1}`}
              effect="blur"
              width="100%"
              height="100%"
              onClick={() => onImageClick(section.photos, index)}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                cursor: 'pointer',
                display: 'block',
              }}
            />
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
        {section.photos.map((photo, index) => (
          <Box
            key={`${photo}-dot`}
            component="button"
            type="button"
            aria-label={`Go to ${section.title} image ${index + 1}`}
            onClick={() => goToPhoto(index)}
            sx={{
              width: activeDot === index ? 22 : 9,
              height: 9,
              border: 0,
              borderRadius: 10,
              cursor: 'pointer',
              backgroundColor:
                activeDot === index ? 'primary.main' : 'grey.400',
              transition: 'all 180ms ease',
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

EventCarousel.propTypes = {
  section: PropTypes.shape({
    title: PropTypes.string.isRequired,
    photos: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  onImageClick: PropTypes.func.isRequired,
};

const GallaryEventGoonj2026 = () => {
  const theme = useTheme();
  const [config, setConfig] = useState({ base_image_url_2026: '' });

  useEffect(() => {
    fetch('/config.json')
      .then((response) => response.json())
      .then((data) => setConfig(data))
      .catch((error) => console.error('Error fetching config:', error));
  }, []);

  const [currentImage, setCurrentImage] = useState(0);
  const [currentGallery, setCurrentGallery] = useState([]);
  const [viewerIsOpen, setViewerIsOpen] = useState(false);

  const openLightbox = (photos, index) => {
    setCurrentGallery(photos);
    setCurrentImage(index);
    setViewerIsOpen(true);
  };

  const closeLightbox = () => {
    setViewerIsOpen(false);
    setCurrentImage(0);
    setCurrentGallery([]);
  };

  // Adornia Photos
  const AdorniaPhotos = [
   `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Adornia/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Adornia/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Adornia/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Adornia/4.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Adornia/5.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Adornia/6.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Adornia/7.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Adornia/8.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Adornia/9.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Adornia/10.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Adornia/11.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Adornia/12.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Adornia/13.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Adornia/14.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Adornia/15.webp`,
    
  ];
  // Charchavedike Photos
  const CharchavedikePhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Charchavedike/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Charchavedike/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Charchavedike/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Charchavedike/4.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Charchavedike/5.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Charchavedike/6.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Charchavedike/7.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Charchavedike/8.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Charchavedike/9.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Charchavedike/10.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Charchavedike/11.webp`,
       
  ];
  // Chetana Nrutya Nataka Photos
  const ChetanaNrutyaNatakaPhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/ChetanaNrutyaNataka/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/ChetanaNrutyaNataka/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/ChetanaNrutyaNataka/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/ChetanaNrutyaNataka/4.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/ChetanaNrutyaNataka/5.webp`,
  ];

  // Debattle Photos
  const DebattlePhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Debattle/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Debattle/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Debattle/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Debattle/4.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Debattle/5.webp`,
     `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Debattle/6.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Debattle/7.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Debattle/8.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Debattle/9.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Debattle/10.webp`,
     `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Debattle/11.webp`,
  ];

  // DELTA - Mind over mystry Photos
  const DeltamindPhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/DELTA-Mind/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/DELTA-Mind/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/DELTA-Mind/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/DELTA-Mind/4.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/DELTA-Mind/5.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/DELTA-Mind/6.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/DELTA-Mind/7.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/DELTA-Mind/8.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/DELTA-Mind/9.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/DELTA-Mind/10.webp`,
  ];

  // DELTA - Science Relay Photos
  const ScienceRelayPhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/DELTA-Science/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/DELTA-Science/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/DELTA-Science/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/DELTA-Science/4.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/DELTA-Science/5.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/DELTA-Science/6.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/DELTA-Science/7.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/DELTA-Science/8.webp`,
  ];
  // Field Fiesta Photos
  const FieldFiestaPhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/FieldFiesta/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/FieldFiesta/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/FieldFiesta/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/FieldFiesta/4.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/FieldFiesta/5.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/FieldFiesta/6.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/FieldFiesta/7.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/FieldFiesta/8.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/FieldFiesta/9.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/FieldFiesta/10.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/FieldFiesta/11.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/FieldFiesta/12.webp`,
  ];
// Finspectra Photos
  const FinspectraPhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Finspectra/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Finspectra/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Finspectra/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Finspectra/4.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Finspectra/5.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Finspectra/6.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Finspectra/7.webp`,
  ];
// Haasya Abhinaya Photos
  const HaasyaAbhinayaPhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/HaasyaAbhinaya/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/HaasyaAbhinaya/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/HaasyaAbhinaya/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/HaasyaAbhinaya/4.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/HaasyaAbhinaya/5.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/HaasyaAbhinaya/6.webp`,
  ];

  // LogoTech - Design Your brand Photos
  const LogoTechPhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/LogoTech/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/LogoTech/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/LogoTech/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/LogoTech/4.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/LogoTech/5.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/LogoTech/6.webp`,
  ];

// Math Escape Room Photos
  const MathEscapeRoomPhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/MathEscapeRoom/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/MathEscapeRoom/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/MathEscapeRoom/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/MathEscapeRoom/4.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/MathEscapeRoom/5.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/MathEscapeRoom/6.webp`,
     `${config.base_image_url_2026}/home/Goonj2026/Photogallery/MathEscapeRoom/7.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/MathEscapeRoom/8.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/MathEscapeRoom/9.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/MathEscapeRoom/10.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/MathEscapeRoom/11.webp`,
  ];

  // Mercatus Dominus Photos
  const MercatusDominusPhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/MercatusDominus/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/MercatusDominus/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/MercatusDominus/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/MercatusDominus/4.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/MercatusDominus/5.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/MercatusDominus/6.webp`,
  ];
// Natya Darpan Photos
  const NatyaDarpanPhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NatyaDarpan/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NatyaDarpan/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NatyaDarpan/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NatyaDarpan/4.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NatyaDarpan/5.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NatyaDarpan/6.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NatyaDarpan/7.webp`,
  ];

// NPSYPR MUN - 2026 - OUTSIDE PARTICIPATING SCHOOLS Photos
  const NPSYPRMUNPhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NPSYPRMUN/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NPSYPRMUN/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NPSYPRMUN/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NPSYPRMUN/4.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NPSYPRMUN/5.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NPSYPRMUN/6.webp`,
     `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NPSYPRMUN/7.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NPSYPRMUN/8.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NPSYPRMUN/9.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NPSYPRMUN/10.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NPSYPRMUN/11.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NPSYPRMUN/12.webp`,
  ];

// NRITYATRUPAKAM SANKRIT KHITIJ PICS Photos
  const NRITYATRUPAKAMPhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NRITYATRUPAKAM/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NRITYATRUPAKAM/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NRITYATRUPAKAM/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NRITYATRUPAKAM/4.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NRITYATRUPAKAM/5.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NRITYATRUPAKAM/6.webp`,
     `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NRITYATRUPAKAM/7.webp`,
      `${config.base_image_url_2026}/home/Goonj2026/Photogallery/NRITYATRUPAKAM/8.webp`,
  ];

// PAGES TO PRESENT
const PagestoprasentPhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Pagestoprasent/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Pagestoprasent/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Pagestoprasent/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Pagestoprasent/4.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Pagestoprasent/5.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Pagestoprasent/6.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Pagestoprasent/7.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Pagestoprasent/8.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Pagestoprasent/9.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Pagestoprasent/10.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Pagestoprasent/11.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Pagestoprasent/12.webp`,
  ];
//PIXEL PARTAHKA photos
const PixelpatahkaPhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Pixelpatahka/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Pixelpatahka/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Pixelpatahka/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Pixelpatahka/4.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Pixelpatahka/5.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Pixelpatahka/6.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Pixelpatahka/7.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Pixelpatahka/8.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Pixelpatahka/9.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Pixelpatahka/10.webp`,
  ];

  // RobomasterPhotos
  const RobomasterPhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Robo/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Robo/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Robo/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Robo/4.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Robo/5.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Robo/6.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Robo/7.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Robo/8.webp`,
  ];

  // SANSADPhotos
    const SANSADPhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Sansad/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Sansad/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Sansad/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Sansad/4.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Sansad/5.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Sansad/6.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Sansad/7.webp`,
  ];

//TerraPhotos
 const TerraPhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Terra/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Terra/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Terra/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Terra/4.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Terra/5.webp`,
  ];

//ThemePhotos
 const ThemePhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Theme/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Theme/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Theme/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Theme/4.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Theme/5.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Theme/6.webp`,
  ];

// ThroneofapolloPhotos
 const ThroneofapolloPhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Throne/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Throne/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Throne/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Throne/4.webp`,
 ];

 // ThroneofapolloPhotos
 const TrailBlazersPhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Trail/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Trail/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Trail/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Trail/4.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Trail/5.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Trail/6.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Trail/7.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Trail/8.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Trail/9.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Trail/10.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Trail/11.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Trail/12.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Trail/13.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Trail/14.webp`,
 ];
 // ThroneofapolloPhotos
 const TrivantaPhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Trivanta/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Trivanta/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Trivanta/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Trivanta/4.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Trivanta/5.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Trivanta/6.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Trivanta/7.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Trivanta/8.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Trivanta/9.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Trivanta/10.webp`,
 ];

 //VenturecruciblePhotos
  const VenturecruciblePhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Venturecrucible/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Venturecrucible/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Venturecrucible/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Venturecrucible/4.webp`,
     `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Venturecrucible/5.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Venturecrucible/6.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Venturecrucible/7.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Venturecrucible/8.webp`,
     `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Venturecrucible/9.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Venturecrucible/10.webp`,
 ];
 //
  const VeritasinternaPhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Veritasinterna/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Veritasinterna/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Veritasinterna/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Veritasinterna/4.webp`,
     `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Veritasinterna/5.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Veritasinterna/6.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Veritasinterna/7.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/Veritasinterna/8.webp`,
 ];
 //
  const VeritasVincitPhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/VeritasVincit/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/VeritasVincit/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/VeritasVincit/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/VeritasVincit/4.webp`,
      `${config.base_image_url_2026}/home/Goonj2026/Photogallery/VeritasVincit/5.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/VeritasVincit/6.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/VeritasVincit/7.webp`,
 ];
 //
  const WebfinitySpacebarPhotos = [
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/WebfinitySpacebar/1.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/WebfinitySpacebar/2.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/WebfinitySpacebar/3.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/WebfinitySpacebar/4.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/WebfinitySpacebar/5.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/WebfinitySpacebar/6.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/WebfinitySpacebar/7.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/WebfinitySpacebar/8.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/WebfinitySpacebar/9.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/WebfinitySpacebar/10.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/WebfinitySpacebar/11.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/WebfinitySpacebar/12.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/WebfinitySpacebar/13.webp`,
    `${config.base_image_url_2026}/home/Goonj2026/Photogallery/WebfinitySpacebar/14.webp`,
 ];

  const gallerySections = [
    {
      title: 'Adornia',
      photos: AdorniaPhotos,
    },
    {
      title: 'Charchavedike photos',
      photos: CharchavedikePhotos,
    },
      {
      title: 'Chetana Nrutya Nataka',
      photos: ChetanaNrutyaNatakaPhotos,
    },
    {
      title: 'Debattle photos',
      photos: DebattlePhotos,
    },
      {
      title: 'DELTA - Mind over mystry',
      photos: DeltamindPhotos,
    },
    {
      title: 'DELTA - Science Relay',
      photos: ScienceRelayPhotos,
    },
      {
      title: 'Field Fiesta ',
      photos: FieldFiestaPhotos,
    },
    {
      title: 'Finspectra',
      photos: FinspectraPhotos,
    },
      {
      title: 'Haasya Abhinaya',
      photos: HaasyaAbhinayaPhotos,
    },
    {
      title: 'LogoTech - Design Your brand',
      photos: LogoTechPhotos,
    },  {
      title: 'Math Escape Room',
      photos: MathEscapeRoomPhotos,
    },
    {
      title: 'Mercatus Dominus',
      photos: MercatusDominusPhotos,
    },  {
      title: 'Natya Darpan',
      photos: NatyaDarpanPhotos,
    },
    {
      title: 'NPSYPR MUN - 2026 - OUTSIDE PARTICIPATING SCHOOLS',
      photos: NPSYPRMUNPhotos,
    },  {
      title: 'NRITYATRUPAKAM SANKRIT KHITIJ PICS',
      photos: NRITYATRUPAKAMPhotos,
    },
    {
      title: 'PAGES TO PRESENT',
      photos: PagestoprasentPhotos,
    },  {
      title: 'PIXEL PARTAHKA',
      photos: PixelpatahkaPhotos,
    },
    {
      title: 'ROBO MATERS -CLOUD ALERT QUEST',
      photos: RobomasterPhotos,
    },  {
      title: 'SANSAD - OUTSIDE PARTICIPATING SCHOOL PICS',
      photos: SANSADPhotos,
    },
    {
      title: 'TERRA TALES',
      photos: TerraPhotos,
    },  {
      title: 'THEME WALK',
      photos: ThemePhotos,
    },
    {
      title: 'THRONE OF APOLLO',
      photos: ThroneofapolloPhotos,
    },  {
      title: 'Trail Blazers',
      photos: TrailBlazersPhotos,
    },
    {
      title: 'Trivanta',
      photos: TrivantaPhotos,
    },  {
      title: 'Venture crucible',
      photos: VenturecruciblePhotos,
    },
    {
      title: 'Veritas interna',
      photos: VeritasinternaPhotos,
    },  {
      title: 'Veritas Vincit',
      photos: VeritasVincitPhotos,
    },
    {
      title: 'Webfinity Spacebar',
      photos: WebfinitySpacebarPhotos,
    },
  ];

  return (
    <Main>
      <Container maxWidth="lg">
        <Box sx={{ mt: 5, mb: { xs: 4, md: 6 } }}>
          <Typography variant="h4" align="center" fontWeight={800}>
            GOONJ 2026 EVENT GALLERY
          </Typography>
        </Box>

        {gallerySections.map((section) => (
          <EventCarousel
            key={section.title}
            section={section}
            onImageClick={openLightbox}
          />
        ))}

        {viewerIsOpen && (
          <Lightbox
            mainSrc={currentGallery[currentImage]}
            nextSrc={
              currentGallery[
                (currentImage + 1) % currentGallery.length
              ]
            }
            prevSrc={
              currentGallery[
                (currentImage + currentGallery.length - 1) %
                  currentGallery.length
              ]
            }
            onCloseRequest={closeLightbox}
            onMoveNextRequest={() =>
              setCurrentImage(
                (currentImage + 1) % currentGallery.length
              )
            }
            onMovePrevRequest={() =>
              setCurrentImage(
                (currentImage + currentGallery.length - 1) %
                  currentGallery.length
              )
            }
            reactModalStyle={{
              overlay: {
                zIndex: 1500,
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(0,0,0,0.92)'
                    : 'rgba(0,0,0,0.86)',
              },
            }}
          />
        )}
      </Container>
    </Main>
  );
};

export default GallaryEventGoonj2026;
